import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Camera, CheckCircle2, Loader2, RefreshCw, X, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Selfie capture with simple in-browser liveness check.
 * The user is prompted through 3 challenges:
 *   1. Hold steady (face detected for 2 sec)
 *   2. Blink (frame-difference detection above threshold)
 *   3. Turn head left then right (frame-difference detection across left/right halves)
 * On completion, captures the final still frame and returns it as a Blob.
 *
 * This is a *practical* liveness gate (motion-based) — not a cryptographically-secure one,
 * but it blocks the most common attack (static photo). For high-security KYC use a 3rd-party
 * service like Digio, Hyperverge, IDfy.
 */
export function SelfieLivenessCheck({ open, onClose, onCapture }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const lastFrameRef = useRef(null);
  const [step, setStep] = useState(0); // 0=intro, 1=hold steady, 2=blink, 3=turn head, 4=capturing, 5=done
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  // Start camera
  useEffect(() => {
    if (!open) return;
    setStep(0); setProgress(0); setError(''); lastFrameRef.current = null;
    let cancelled = false;
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'user', width: { ideal: 640 }, height: { ideal: 480 } },
          audio: false,
        });
        if (cancelled) { stream.getTracks().forEach(t => t.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
        }
      } catch (err) {
        console.error('Camera error:', err);
        setError(err.name === 'NotAllowedError'
          ? 'Camera permission denied. Please allow camera access and try again.'
          : 'Could not start camera: ' + (err.message || err.name));
      }
    })();
    return () => {
      cancelled = true;
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(t => t.stop());
        streamRef.current = null;
      }
    };
  }, [open]);

  // Compute average difference between two frames (used to detect motion / blinks)
  const frameDiff = (a, b, leftHalfOnly = false, rightHalfOnly = false) => {
    if (!a || !b || a.length !== b.length) return 0;
    let sum = 0; let count = 0;
    const width = 640; // sampled buffer width
    for (let i = 0; i < a.length; i += 4) {
      const px = (i / 4) % width;
      if (leftHalfOnly && px > width / 2) continue;
      if (rightHalfOnly && px < width / 2) continue;
      sum += Math.abs(a[i] - b[i]) + Math.abs(a[i + 1] - b[i + 1]) + Math.abs(a[i + 2] - b[i + 2]);
      count++;
    }
    return count > 0 ? sum / count : 0;
  };

  // Grab current frame as ImageData
  const grabFrame = () => {
    const v = videoRef.current, c = canvasRef.current;
    if (!v || !c || v.readyState < 2) return null;
    const w = 640, h = 480;
    c.width = w; c.height = h;
    const ctx = c.getContext('2d');
    ctx.drawImage(v, 0, 0, w, h);
    return ctx.getImageData(0, 0, w, h).data;
  };

  // Run liveness pipeline on user click
  const startLiveness = async () => {
    setStep(1); setProgress(0); setError('');
    try {
      // Step 1: hold steady (2 sec, low motion expected)
      await new Promise(res => setTimeout(res, 800));
      const baseline = grabFrame();
      if (!baseline) throw new Error('Camera not ready');
      lastFrameRef.current = baseline;
      for (let i = 0; i < 20; i++) {
        setProgress(Math.min(100, (i + 1) * 5));
        await new Promise(res => setTimeout(res, 100));
      }

      // Step 2: blink
      setStep(2); setProgress(0);
      let blinkDetected = false;
      let prev = grabFrame();
      for (let i = 0; i < 50 && !blinkDetected; i++) {
        await new Promise(res => setTimeout(res, 100));
        const cur = grabFrame();
        if (cur && prev) {
          const d = frameDiff(prev, cur);
          if (d > 5) blinkDetected = true; // detected motion above threshold
        }
        prev = cur;
        setProgress(Math.min(100, (i + 1) * 2));
      }
      if (!blinkDetected) throw new Error('No motion detected. Please blink and try again.');

      // Step 3: turn head left / right
      setStep(3); setProgress(0);
      let leftMotion = 0; let rightMotion = 0;
      prev = grabFrame();
      for (let i = 0; i < 60; i++) {
        await new Promise(res => setTimeout(res, 100));
        const cur = grabFrame();
        if (cur && prev) {
          leftMotion = Math.max(leftMotion, frameDiff(prev, cur, true, false));
          rightMotion = Math.max(rightMotion, frameDiff(prev, cur, false, true));
        }
        prev = cur;
        setProgress(Math.min(100, (i + 1) * (100 / 60)));
        if (leftMotion > 6 && rightMotion > 6) break;
      }
      if (leftMotion < 6 || rightMotion < 6) throw new Error('Head turn not detected. Please turn your head left then right.');

      // Step 4: capture final still
      setStep(4);
      await new Promise(res => setTimeout(res, 500));
      const c = canvasRef.current;
      const blob = await new Promise(res => c.toBlob(res, 'image/jpeg', 0.9));
      if (!blob) throw new Error('Failed to capture image');
      setStep(5);
      setBusy(true);
      await onCapture(blob);
      setBusy(false);
      onClose();
    } catch (err) {
      console.error('Liveness check failed:', err);
      setError(err.message || 'Liveness check failed. Please try again.');
      setStep(0);
      setBusy(false);
    }
  };

  const labels = {
    0: { title: 'Selfie Verification', sub: 'A quick 3-step check confirms it\'s really you.' },
    1: { title: 'Hold Steady', sub: 'Keep your face centered in the frame.' },
    2: { title: 'Please Blink', sub: 'Blink once or twice naturally.' },
    3: { title: 'Turn Head Left, Then Right', sub: 'Slowly turn your head side to side.' },
    4: { title: 'Capturing...', sub: 'Almost done!' },
    5: { title: 'Verified!', sub: 'Uploading your selfie.' },
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="liveness-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Camera size={18} className="text-brand-primary" /> {labels[step].title}
          </DialogTitle>
          <p className="text-xs text-brand-text-secondary">{labels[step].sub}</p>
        </DialogHeader>
        <div className="space-y-3">
          <div className="relative rounded-lg overflow-hidden bg-black aspect-[4/3]">
            <video ref={videoRef} playsInline muted className="w-full h-full object-cover scale-x-[-1]" />
            <canvas ref={canvasRef} className="hidden" />
            {/* Face oval guide */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className={`border-4 rounded-full transition-colors ${step === 5 ? 'border-brand-success' : step >= 1 ? 'border-brand-primary' : 'border-white/50'}`}
                style={{ width: '60%', height: '80%' }} />
            </div>
            {step === 5 && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/60">
                <CheckCircle2 size={48} className="text-brand-success" />
              </div>
            )}
          </div>
          {step >= 1 && step <= 3 && (
            <div className="w-full bg-brand-bg rounded-full h-2 overflow-hidden">
              <div className="h-full bg-brand-primary transition-all" style={{ width: `${progress}%` }} />
            </div>
          )}
          {error && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 border border-red-200" data-testid="liveness-error">
              <AlertCircle size={14} className="text-brand-error flex-shrink-0 mt-0.5" />
              <p className="text-xs text-brand-error leading-snug">{error}</p>
            </div>
          )}
          <div className="flex gap-2">
            {step === 0 && (
              <Button onClick={startLiveness} disabled={!!error || busy} className="flex-1 bg-brand-primary text-white rounded-lg" data-testid="start-liveness-btn">
                <Camera size={14} className="mr-2" /> Start Verification
              </Button>
            )}
            {(error || step > 0) && step < 5 && (
              <Button variant="outline" onClick={() => { setError(''); setStep(0); }} className="rounded-lg" data-testid="retry-liveness-btn">
                <RefreshCw size={12} className="mr-1" /> Retry
              </Button>
            )}
            <Button variant="ghost" onClick={onClose} className="rounded-lg" data-testid="close-liveness-btn">
              <X size={12} className="mr-1" /> Cancel
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
